// The 3D viewer (F01/F02/F03): framework-free three.js scene, loading, picking, camera.
// The UI drives it through this class's methods and receives events through callbacks.
import {
  ACESFilmicToneMapping, BackSide, DoubleSide, Box3, BufferGeometry, Color, DirectionalLight, Float32BufferAttribute, Group, HemisphereLight, Material,
  Matrix3, Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D, PerspectiveCamera, Raycaster, Scene, SRGBColorSpace,
  Vector2, Vector3, WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';
import { LAYERS, LAYER_BY_ID, colorFor, type LayerId, type LayerState } from './layers';

// Note: MeshoptDecoder.useWorkers() breaks under minification (its worker source is stringified), so decode on the main thread.
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;

export type Side = 'l' | 'r' | 'm';
export interface Vec3Tuple { 0: number; 1: number; 2: number; length: 3 }
export interface CameraState { position: [number, number, number]; target: [number, number, number] }
/** 'f' or 'm': a structure that exists in one body only (F22); absent means both. */
export type Body = 'm' | 'f';
export interface StructureGeo { id: string; layer: LayerId; region: string; sides: Side[]; selectable: boolean; bbox: { min: number[]; max: number[] }; body?: Body }
export interface Chunk { id: string; system: string; layer: LayerId; lod: 'lo' | 'hi'; region: string | null; url: string; bytes: number; nodes: string[]; body?: Body }
export interface AssetManifest {
  chunks: Chunk[];
  structures: Record<string, StructureGeo>;
  copyright: string;
  /** Female figure (F22): its chunks, and the layers it has parts in. */
  bodies?: { f?: { chunks: string[]; layers: LayerId[] } };
}
export type IsolateMode = 'alone' | 'skeleton' | 'region';
export interface Visibility { hidden: ReadonlySet<string>; isolate: { mode: IsolateMode; ids: string[]; region?: string } | null; hideSensitive?: ReadonlySet<string> }
export interface Pick { id: string; side: Side; point: [number, number, number] }
export type ViewPreset = 'front' | 'back' | 'left' | 'right' | 'top';

export class WebGLUnavailableError extends Error {
  constructor() {
    super('WebGL2 is not available');
  }
}

export interface ViewerEvents {
  onPick?: (p: Pick | null) => void;
  onHover?: (p: Pick | null, clientX: number, clientY: number) => void;
  onCameraChange?: (c: CameraState) => void;
  onLayerLoaded?: (layer: LayerId, loaded: number, total: number) => void;
  onFirstRender?: () => void;
}

interface Part { mesh: Mesh; id: string; side: Side; layer: LayerId; region: string; color: string; body?: Body }

const BODY_CENTER = new Vector3(0, 0.86, 0);

/**
 * Converts quantised (normalised integer) positions to Float32. three-mesh-bvh builds wrong bounds for some
 * normalised-integer meshes (thin parts became unpickable); floats cost ~12 MB and make picking exact.
 */
function dequantizePositions(geo: BufferGeometry): void {
  // Mirrored L/R meshes share one index buffer after dedup; give each its own so per-mesh BVHs can't interfere.
  if (geo.index) geo.setIndex(geo.index.clone());
  const attr = geo.getAttribute('position');
  if (!attr || attr.array instanceof Float32Array) return;
  const out = new Float32Array(attr.count * 3);
  for (let i = 0; i < attr.count; i++) {
    out[i * 3] = attr.getX(i);
    out[i * 3 + 1] = attr.getY(i);
    out[i * 3 + 2] = attr.getZ(i);
  }
  geo.setAttribute('position', new Float32BufferAttribute(out, 3));
}
const ease = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export class Viewer {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(32, 1, 0.01, 50);
  renderer!: WebGLRenderer;
  controls!: OrbitControls;
  private root = new Group();
  private loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  private parts = new Map<string, Part>(); // node name → part
  private byId = new Map<string, Part[]>();
  private layerState: Record<LayerId, LayerState> = Object.fromEntries(LAYERS.map((l) => [l.id, l.initial])) as Record<LayerId, LayerState>;
  private visibility: Visibility = { hidden: new Set(), isolate: null };
  private selected: { id: string; side?: Side } | null = null;
  private highlighted = new Set<string>();
  private outlines: Mesh[] = [];
  private materials = new Map<string, Material>();
  private outlineMaterial!: MeshBasicMaterial;
  private needsRender = true;
  private anim: { from: CameraState; to: CameraState; start: number; dur: number } | null = null;
  private raf = 0;
  private loadedChunks = new Set<string>();
  private regionsLoading = new Map<string, Promise<void>>();
  private firstRendered = false;
  /** While true, scene re-renders are deferred (bulk loading); camera interaction still renders. */
  private holdFrames = false;
  private raycaster = new Raycaster();
  private insets = { right: 0, bottom: 0 };
  reducedMotion = false;

  constructor(private container: HTMLElement, private manifest: AssetManifest, private events: ViewerEvents = {}) {}

  static supported(): boolean {
    try {
      const c = document.createElement('canvas');
      return !!c.getContext('webgl2');
    } catch {
      return false;
    }
  }

  init(): void {
    if (!Viewer.supported()) throw new WebGLUnavailableError();
    const r = new WebGLRenderer({ antialias: true, preserveDrawingBuffer: false, powerPreference: 'high-performance' });
    r.outputColorSpace = SRGBColorSpace;
    r.toneMapping = ACESFilmicToneMapping;
    r.toneMappingExposure = 1.05;
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    r.setClearColor(0x000000, 0);
    r.domElement.setAttribute('data-testid', 'viewer-canvas');
    r.domElement.setAttribute('aria-label', '3D body. Use the search or the parts list to explore without a pointer.');
    r.domElement.setAttribute('role', 'img');
    this.renderer = r;
    this.container.appendChild(r.domElement);

    this.scene.add(new HemisphereLight(0xffffff, 0x8a7f74, 1.9));
    const key = new DirectionalLight(0xffffff, 1.5);
    key.position.set(1.2, 2.5, 3);
    const rim = new DirectionalLight(0xfff1e6, 0.7);
    rim.position.set(-2, 1.5, -2.5);
    this.scene.add(key, rim, this.root);

    this.controls = new OrbitControls(this.camera, r.domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.12;
    this.controls.minDistance = 0.12;
    this.controls.maxDistance = 6;
    this.controls.zoomToCursor = true;
    this.controls.addEventListener('change', () => this.onControlsChange());

    this.outlineMaterial = new MeshBasicMaterial({ color: 0xe0674c, side: BackSide });
    this.outlineMaterial.onBeforeCompile = (s) => {
      s.vertexShader = s.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\ntransformed += normalize(normal) * 0.0025;');
    };

    this.resize();
    new ResizeObserver(() => this.resize()).observe(this.container);
    this.setPreset('front', false);
    this.bindPointer();
    const loop = () => {
      this.raf = requestAnimationFrame(loop);
      this.tick();
    };
    loop();
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.controls.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  // ---------- loading ----------

  /** Loads the default (lo) chunk of each layer in load order; resolves when all are in. */
  async loadDefault(layers: LayerId[] = LAYERS.map((l) => l.id)): Promise<void> {
    const order = [...layers].sort((a, b) => LAYER_BY_ID[a].loadOrder - LAYER_BY_ID[b].loadOrder);
    const chunks = order.flatMap((l) => this.manifest.chunks.filter((c) => c.layer === l && c.lod === 'lo' && !c.body));
    let done = 0;
    // Skin shell and skeleton first so a recognisable body appears quickly; the rest in parallel.
    const firstLayers = new Set<LayerId>(['skin', 'skeletal']);
    const first = chunks.filter((c) => firstLayers.has(c.layer));
    const rest = chunks.filter((c) => !firstLayers.has(c.layer));
    await Promise.all(first.map((c) => this.loadChunk(c).then(() => this.events.onLayerLoaded?.(c.layer, ++done, chunks.length))));
    // Show the skeleton now, then hold further frames until everything is in (each frame is costly on slow GPUs).
    this.requestRender();
    this.tick();
    this.holdFrames = true;
    try {
      await Promise.all(
        rest.map((c) =>
          this.loadChunk(c).then(() => this.events.onLayerLoaded?.(c.layer, ++done, chunks.length)),
        ),
      );
    } finally {
      this.holdFrames = false;
      this.requestRender();
    }
  }

  /** In-flight and finished chunk loads: a second request for the same chunk waits for the first. */
  private chunkLoads = new Map<string, Promise<void>>();

  private loadChunk(chunk: Chunk): Promise<void> {
    let p = this.chunkLoads.get(chunk.id);
    if (!p) {
      p = this.fetchChunk(chunk);
      this.chunkLoads.set(chunk.id, p);
    }
    return p;
  }

  private async fetchChunk(chunk: Chunk): Promise<void> {
    this.loadedChunks.add(chunk.id);
    const gltf = await this.loader.loadAsync(chunk.url);
    const meshes: Mesh[] = [];
    gltf.scene.traverse((o: Object3D) => {
      if ((o as Mesh).isMesh) meshes.push(o as Mesh);
    });
    let sliceStart = performance.now();
    for (const m of meshes) {
      // Yield to the browser every ~8 ms so a big chunk never blocks input (long-task budget).
      if (performance.now() - sliceStart > 8) {
        await new Promise((r) => setTimeout(r, 0));
        sliceStart = performance.now();
      }
      dequantizePositions(m.geometry);
      const name = (m.userData?.id ? m.name : m.parent?.name) || m.name;
      const extras = (m.userData?.id ? m.userData : m.parent?.userData) as { id: string; side: Side; body?: Body };
      const id = extras?.id ?? name;
      const existing = this.parts.get(name);
      if (chunk.lod === 'hi' && existing) {
        // Swap in full-detail geometry; keep the same mesh object, material and state.
        existing.mesh.geometry.disposeBoundsTree();
        existing.mesh.geometry.dispose();
        existing.mesh.geometry = m.geometry;
        m.updateWorldMatrix(true, false);
        m.matrixWorld.decompose(existing.mesh.position, existing.mesh.quaternion, existing.mesh.scale);
        existing.mesh.updateMatrix();
        continue;
      }
      if (existing) continue;
      const s = this.manifest.structures[id];
      const layer = (s?.layer ?? chunk.layer) as LayerId;
      m.updateWorldMatrix(true, false);
      m.matrixWorld.decompose(m.position, m.quaternion, m.scale);
      m.removeFromParent();
      m.name = name;
      m.userData = { id, side: extras?.side ?? 'm', body: extras?.body };
      m.matrixAutoUpdate = false;
      m.updateMatrix();
      const part: Part = { mesh: m, id, side: extras?.side ?? 'm', layer, region: s?.region ?? chunk.region ?? '', color: colorFor(id, layer), body: extras?.body ?? chunk.body };
      this.parts.set(name, part);
      if (!this.byId.has(id)) this.byId.set(id, []);
      this.byId.get(id)!.push(part);
      this.root.add(m);
      this.applyPart(part);
    }
    this.refreshOutline();
    this.requestRender();
  }

  /** Loads full-detail chunks for a region (all currently loaded layers). */
  loadRegion(region: string): Promise<void> {
    if (this.regionsLoading.has(region)) return this.regionsLoading.get(region)!;
    const layersLoaded = new Set(this.manifest.chunks.filter((c) => c.lod === 'lo' && this.loadedChunks.has(c.id)).map((c) => c.layer));
    const chunks = this.manifest.chunks.filter((c) => c.lod === 'hi' && !c.body && c.region === region && layersLoaded.has(c.layer));
    const p = Promise.all(chunks.map((c) => this.loadChunk(c))).then(() => undefined);
    this.regionsLoading.set(region, p);
    return p;
  }

  /** Builds picking BVHs in idle time so the first hover, tap or reveal is instant. */
  /** True while picking BVHs are still being built in idle time (tests wait for a quiet main thread). */
  warming = false;

  warmUp(): void {
    const queue = [...this.parts.values()].map((p) => p.mesh.geometry);
    this.warming = queue.length > 0;
    const idle = (cb: () => void) => ('requestIdleCallback' in window ? (window as Window & { requestIdleCallback: (f: () => void) => void }).requestIdleCallback(cb) : setTimeout(cb, 16));
    const step = () => {
      const end = performance.now() + 8;
      while (queue.length && performance.now() < end) {
        const g = queue.shift()!;
        if (!g.boundsTree) g.computeBoundsTree();
      }
      if (queue.length) idle(step);
      else this.warming = false;
    };
    idle(step);
  }

  // ---------- body (F22) ----------
  private body: Body = 'm';
  private bodyLoading: Promise<void> = Promise.resolve();

  /** Resolves once the body last asked for is loaded and shown (framing and reveal wait for it). */
  bodyReady(): Promise<void> {
    return this.bodyLoading;
  }

  getBody(): Body {
    return this.body;
  }

  /** Whether a mesh, or a structure's geometry-free description, belongs to the figure on show. */
  private inBody(p: { id: string; body?: Body; mesh?: Mesh }): boolean {
    // The female figure is separate: its meshes come from the female chunks only, and share ids with the male ones.
    if ('mesh' in p && p.mesh) return (p.body === 'f') === (this.body === 'f');
    const only = this.manifest.structures[p.id]?.body;
    return !only || only === this.body;
  }

  /** The meshes of a structure in the figure on show (both figures can have a part with the same id). */
  partsOf(id: string): Part[] {
    return (this.byId.get(id) ?? []).filter((p) => this.inBody(p));
  }

  /** True if any part of the structure is drawn right now (tests, UI hints). */
  isShown(id: string): boolean {
    return this.partsOf(id).some((p) => p.mesh.visible);
  }

  /** Switches body. The female chunks load the first time they are needed and are then kept. */
  setBody(body: Body): Promise<void> {
    this.bodyLoading = this.applyBody(body);
    return this.bodyLoading;
  }

  private async applyBody(body: Body): Promise<void> {
    if (body === 'f') {
      const f = this.manifest.bodies?.f;
      if (f) {
        await Promise.all(this.manifest.chunks.filter((c) => f.chunks.includes(c.id)).map((c) => this.loadChunk(c)));
      }
    }
    this.body = body;
    for (const p of this.parts.values()) this.applyPart(p);
    this.refreshOutline();
    this.requestRender();
  }

  isLoaded(id: string): boolean {
    return this.byId.has(id);
  }

  // ---------- visual state ----------

  setLayers(state: Record<LayerId, LayerState>): void {
    this.layerState = { ...state };
    for (const p of this.parts.values()) this.applyPart(p);
    this.refreshOutline();
    this.requestRender();
  }

  setVisibility(v: Visibility): void {
    this.visibility = v;
    for (const p of this.parts.values()) this.applyPart(p);
    this.refreshOutline();
    this.requestRender();
  }

  select(id: string | null, side?: Side): void {
    const prev = this.selected;
    this.selected = id ? { id, side } : null;
    for (const pid of new Set([prev?.id, id].filter(Boolean) as string[])) for (const p of this.byId.get(pid) ?? []) this.applyPart(p);
    this.refreshOutline();
    this.requestRender();
  }

  /** Tints and outlines several structures at once (tour stops). */
  setHighlight(ids: string[]): void {
    const prev = this.highlighted;
    this.highlighted = new Set(ids);
    for (const id of new Set([...prev, ...ids])) for (const p of this.byId.get(id) ?? []) this.applyPart(p);
    this.refreshOutline();
    this.requestRender();
  }

  /** Resolved display mode of a part given layer state, hidden set, isolation and selection. */
  modeOf(p: Pick | Part | { id: string; layer: LayerId; region: string }): 'on' | 'ghost' | 'off' {
    const { hidden, isolate, hideSensitive } = this.visibility;
    if (hidden.has(p.id) || hideSensitive?.has(p.id)) return 'off';
    if (!this.inBody(p)) return 'off';
    const layer = 'layer' in p ? p.layer : this.manifest.structures[p.id]?.layer;
    if (isolate) {
      if (isolate.ids.includes(p.id)) return 'on';
      if (isolate.mode === 'skeleton' && layer === 'skeletal') return 'ghost';
      if (isolate.mode === 'region' && 'region' in p && p.region === isolate.region && this.layerState[layer as LayerId] !== 'off') return this.layerState[layer as LayerId];
      return 'off';
    }
    const st = this.layerState[layer as LayerId] ?? 'off';
    // The selected or highlighted structure is always shown solid, even if its layer is ghosted.
    if ((this.selected?.id === p.id || this.highlighted.has(p.id)) && st !== 'off') return 'on';
    return st;
  }

  private material(color: string, mode: 'on' | 'ghost', selected: boolean, layer: LayerId): Material {
    const key = `${color}|${mode}|${selected}|${layer}`;
    let m = this.materials.get(key);
    if (!m) {
      // Double-sided: thin source sheets (membranes, simplified tubes) must not vanish when seen from behind.
      const mat = new MeshStandardMaterial({ color: new Color(color), roughness: layer === 'skeletal' ? 0.82 : 0.62, metalness: 0, side: DoubleSide });
      if (mode === 'ghost') {
        mat.transparent = true;
        mat.opacity = layer === 'skin' ? 0.13 : 0.18;
        mat.depthWrite = false;
      }
      if (selected) {
        mat.emissive = new Color(0xe0674c);
        mat.emissiveIntensity = 0.28;
      }
      m = mat;
      this.materials.set(key, m);
    }
    return m;
  }

  private applyPart(p: Part): void {
    const mode = this.modeOf(p);
    p.mesh.visible = mode !== 'off';
    if (mode === 'off') return;
    const sel = (this.selected?.id === p.id && (!this.selected.side || this.selected.side === p.side || p.side === 'm')) || this.highlighted.has(p.id);
    p.mesh.material = this.material(p.color, mode, sel, p.layer);
    p.mesh.renderOrder = mode === 'ghost' ? 10 + (p.layer === 'skin' ? 5 : LAYER_BY_ID[p.layer].depth) : 0;
  }

  private refreshOutline(): void {
    for (const o of this.outlines) o.removeFromParent();
    this.outlines = [];
    const sel = this.selected;
    const parts = [
      ...(sel ? this.partsOf(sel.id).filter((p) => !sel.side || p.side === sel.side || p.side === 'm') : []),
      ...[...this.highlighted].flatMap((id) => this.partsOf(id)),
    ];
    for (const p of parts) {
      if (!p.mesh.visible) continue;
      const o = new Mesh(p.mesh.geometry, this.outlineMaterial);
      o.raycast = () => {};
      o.name = '__outline';
      p.mesh.add(o);
      this.outlines.push(o);
    }
  }

  // ---------- picking ----------

  /** Picks the frontmost visible, non-ghosted, selectable structure at a client (CSS px) position. */
  pick(clientX: number, clientY: number): Pick | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new Vector2(((clientX - rect.left) / rect.width) * 2 - 1, -((clientY - rect.top) / rect.height) * 2 + 1);
    return this.pickNdc(ndc.x, ndc.y);
  }

  pickNdc(x: number, y: number): Pick | null {
    this.camera.updateMatrixWorld();
    this.raycaster.setFromCamera(new Vector2(x, y), this.camera);
    this.raycaster.firstHitOnly = true;
    const candidates: Mesh[] = [];
    for (const p of this.parts.values()) {
      if (!p.mesh.visible || this.modeOf(p) !== 'on') continue;
      if (!this.manifest.structures[p.id]?.selectable) continue;
      if (!p.mesh.geometry.boundsTree) p.mesh.geometry.computeBoundsTree();
      candidates.push(p.mesh);
    }
    const hits = this.raycaster.intersectObjects(candidates, false);
    const h = hits[0];
    if (!h) return null;
    const u = h.object.userData as { id: string; side: Side };
    return { id: u.id, side: u.side, point: [h.point.x, h.point.y, h.point.z] };
  }

  /**
   * Layers whose visible structures block the view of `id` from a camera position (F02/F03 "reveal").
   * Rays go from the camera to sample points on the structure's surface; any other solid structure hit
   * first marks its layer as occluding.
   */
  occludingLayers(id: string, cam: CameraState, side?: Side): LayerId[] {
    const parts = this.partsOf(id).filter((p) => !side || p.side === side || p.side === 'm');
    if (!parts.length) return [];
    const origin = new Vector3(...cam.position);
    // Sample only camera-facing surface points: those are what the reader would see.
    const samples: Vector3[] = [];
    for (const p of parts) {
      const pos = p.mesh.geometry.getAttribute('position');
      const nor = p.mesh.geometry.getAttribute('normal');
      const nm = new Matrix3().getNormalMatrix(p.mesh.matrixWorld);
      const step = Math.max(1, Math.floor(pos.count / 120));
      for (let i = 0; i < pos.count; i += step) {
        const pt = new Vector3().fromBufferAttribute(pos, i).applyMatrix4(p.mesh.matrixWorld);
        if (nor) {
          const n = new Vector3().fromBufferAttribute(nor, i).applyMatrix3(nm);
          if (n.dot(origin.clone().sub(pt)) <= 0) continue;
        }
        samples.push(pt);
      }
    }
    if (!samples.length) return [];
    const blockers: Mesh[] = [];
    for (const p of this.parts.values()) {
      if (p.id === id || !p.mesh.visible || this.modeOf(p) !== 'on') continue;
      if (!p.mesh.geometry.boundsTree) p.mesh.geometry.computeBoundsTree();
      blockers.push(p.mesh);
    }
    const counts = new Map<LayerId, number>();
    const rc = new Raycaster();
    for (const pt of samples) {
      const dir = pt.clone().sub(origin);
      const dist = dir.length();
      rc.set(origin, dir.normalize());
      rc.far = dist - 0.004;
      // Every layer in the way counts, not just the nearest: a part behind both muscle and bone needs both faded.
      const layers = new Set<LayerId>();
      for (const hit of rc.intersectObjects(blockers, false)) {
        const layer = this.parts.get(hit.object.name)?.layer;
        if (layer) layers.add(layer);
      }
      for (const layer of layers) counts.set(layer, (counts.get(layer) ?? 0) + 1);
    }
    // A layer counts as occluding if it blocks at least 15% of the samples.
    return [...counts.entries()].filter(([, n]) => n >= samples.length * 0.15).map(([l]) => l);
  }

  /** Screen-space (CSS px, relative to canvas) position of a world point. */
  project(point: [number, number, number]): { x: number; y: number } {
    this.camera.updateMatrixWorld();
    const v = new Vector3(...point).project(this.camera);
    const rect = this.renderer.domElement.getBoundingClientRect();
    return { x: ((v.x + 1) / 2) * rect.width + rect.left, y: ((1 - v.y) / 2) * rect.height + rect.top };
  }

  /** A point on the surface of a structure near its centre: the centroid of the closest triangle (tests, label anchors). */
  surfacePoint(id: string, side?: Side): [number, number, number] | null {
    const parts = this.partsOf(id).filter((p) => !side || p.side === side || p.side === 'm');
    const p = parts[0];
    if (!p) return null;
    p.mesh.updateMatrixWorld();
    const c = new Box3().setFromObject(p.mesh).getCenter(new Vector3());
    const pos = p.mesh.geometry.getAttribute('position');
    const index = p.mesh.geometry.index;
    const triCount = index ? index.count / 3 : pos.count / 3;
    const va = new Vector3(), vb = new Vector3(), vc = new Vector3(), centroid = new Vector3(), out = new Vector3();
    let best = Infinity;
    const step = Math.max(1, Math.floor(triCount / 4000));
    for (let t = 0; t < triCount; t += step) {
      const i0 = index ? index.getX(t * 3) : t * 3;
      const i1 = index ? index.getX(t * 3 + 1) : t * 3 + 1;
      const i2 = index ? index.getX(t * 3 + 2) : t * 3 + 2;
      va.fromBufferAttribute(pos, i0).applyMatrix4(p.mesh.matrixWorld);
      vb.fromBufferAttribute(pos, i1).applyMatrix4(p.mesh.matrixWorld);
      vc.fromBufferAttribute(pos, i2).applyMatrix4(p.mesh.matrixWorld);
      centroid.copy(va).add(vb).add(vc).divideScalar(3);
      const d = centroid.distanceToSquared(c);
      if (d < best) {
        best = d;
        out.copy(centroid);
      }
    }
    return [out.x, out.y, out.z];
  }

  private bindPointer(): void {
    const el = this.renderer.domElement;
    let down: { x: number; y: number; t: number } | null = null;
    el.addEventListener('pointerdown', (e) => {
      down = { x: e.clientX, y: e.clientY, t: performance.now() };
    });
    el.addEventListener('pointerup', (e) => {
      if (!down) return;
      const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
      const quick = performance.now() - down.t < 500;
      down = null;
      if (moved < 6 && quick && e.button === 0) this.events.onPick?.(this.pick(e.clientX, e.clientY));
    });
    el.addEventListener('dblclick', (e) => {
      const p = this.pick(e.clientX, e.clientY);
      if (p) this.animateTo({ position: this.cameraState().position, target: p.point });
    });
    let last = 0;
    el.addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse' || e.buttons) return;
      const now = performance.now();
      if (now - last < 40) return;
      last = now;
      this.events.onHover?.(this.pick(e.clientX, e.clientY), e.clientX, e.clientY);
    });
    el.addEventListener('pointerleave', () => this.events.onHover?.(null, 0, 0));
  }

  // ---------- camera ----------

  cameraState(): CameraState {
    const p = this.camera.position, t = this.controls.target;
    return { position: [p.x, p.y, p.z], target: [t.x, t.y, t.z] };
  }

  setCamera(c: CameraState, animate = true): void {
    if (animate && !this.reducedMotion) this.animateTo(c);
    else {
      this.anim = null;
      this.camera.position.set(...c.position);
      this.controls.target.set(...c.target);
      this.controls.update();
      this.requestRender();
    }
  }

  presetCamera(preset: ViewPreset, center = BODY_CENTER, distance = 4.1): CameraState {
    const dir: Record<ViewPreset, [number, number, number]> = {
      front: [0, 0, 1], back: [0, 0, -1], left: [1, 0, 0], right: [-1, 0, 0], top: [0, 1, 0.001],
    };
    const d = dir[preset];
    return {
      position: [center.x + d[0] * distance, center.y + d[1] * distance, center.z + d[2] * distance],
      target: [center.x, center.y, center.z],
    };
  }

  setPreset(preset: ViewPreset, animate = true): void {
    this.setCamera(this.presetCamera(preset), animate);
  }

  reset(animate = true): void {
    this.setPreset('front', animate);
  }

  /** Flies the camera to frame a structure (both sides, or one side), keeping the current viewing direction. */
  frame(id: string, opts: { side?: Side; animate?: boolean; preset?: ViewPreset; zoom?: number } = {}): CameraState | null {
    const s = this.manifest.structures[id];
    if (!s) return null;
    const box = new Box3();
    const parts = this.partsOf(id).filter((p) => !opts.side || p.side === opts.side || p.side === 'm');
    if (parts.length) for (const p of parts) box.expandByObject(p.mesh);
    else box.set(new Vector3(...s.bbox.min), new Vector3(...s.bbox.max));
    const center = box.getCenter(new Vector3());
    const radius = Math.max(0.03, box.getSize(new Vector3()).length() / 2);
    const fov = (this.camera.fov * Math.PI) / 180;
    const dist = Math.max(0.3, Math.min(4.5, (radius / Math.sin(fov / 2)) * 1.9)) / (opts.zoom ?? 1);
    let dir: Vector3;
    if (opts.preset) {
      const pc = this.presetCamera(opts.preset, center, 1);
      dir = new Vector3(...pc.position).sub(center).normalize();
    } else {
      dir = this.camera.position.clone().sub(this.controls.target).normalize();
      // Structures at the back of the body look better from behind.
      if (center.z < -0.06 && dir.z > 0.5) dir.set(dir.x, dir.y, -dir.z);
    }
    const target: CameraState = { position: center.clone().addScaledVector(dir, dist).toArray() as [number, number, number], target: center.toArray() as [number, number, number] };
    this.setCamera(target, opts.animate ?? true);
    if (s.region) void this.loadRegion(s.region).then(() => this.requestRender());
    return target;
  }

  orbitBy(dAzimuth: number, dPolar: number): void {
    const off = this.camera.position.clone().sub(this.controls.target);
    const r = off.length();
    let theta = Math.atan2(off.x, off.z) + dAzimuth;
    let phi = Math.acos(Math.max(-1, Math.min(1, off.y / r))) + dPolar;
    phi = Math.max(0.05, Math.min(Math.PI - 0.05, phi));
    theta = ((theta + Math.PI) % (2 * Math.PI)) - Math.PI;
    const t = this.controls.target;
    this.setCamera({ position: [t.x + r * Math.sin(phi) * Math.sin(theta), t.y + r * Math.cos(phi), t.z + r * Math.sin(phi) * Math.cos(theta)], target: [t.x, t.y, t.z] }, false);
  }

  zoomBy(factor: number): void {
    const t = this.controls.target;
    const off = this.camera.position.clone().sub(t);
    const len = Math.max(this.controls.minDistance, Math.min(this.controls.maxDistance, off.length() * factor));
    off.setLength(len);
    this.setCamera({ position: [t.x + off.x, t.y + off.y, t.z + off.z], target: [t.x, t.y, t.z] }, false);
  }

  private animateTo(to: CameraState): void {
    if (this.reducedMotion) return this.setCamera(to, false);
    this.anim = { from: this.cameraState(), to, start: performance.now(), dur: 520 };
    this.requestRender();
  }

  /** Keeps the visible body centred in the area not covered by panels (CSS px). */
  setInsets(insets: { right?: number; bottom?: number }): void {
    this.insets = { right: insets.right ?? 0, bottom: insets.bottom ?? 0 };
    this.resize();
  }

  private onControlsChange(): void {
    // Keep the orbit target inside the body's neighbourhood so the body can't be lost off-screen.
    const t = this.controls.target;
    t.x = Math.max(-0.6, Math.min(0.6, t.x));
    t.y = Math.max(-0.1, Math.min(1.9, t.y));
    t.z = Math.max(-0.5, Math.min(0.5, t.z));
    this.requestRender();
    this.events.onCameraChange?.(this.cameraState());
  }

  resize(): void {
    if (!this.renderer) return;
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.renderer.domElement.style.width = '100%';
    this.renderer.domElement.style.height = '100%';
    this.camera.aspect = w / h;
    const { right, bottom } = this.insets;
    if (right || bottom) this.camera.setViewOffset(w, h, right / 2, bottom / 2, w, h);
    else this.camera.clearViewOffset();
    this.camera.updateProjectionMatrix();
    this.requestRender();
  }

  requestRender(): void {
    this.needsRender = true;
  }

  private tick(): void {
    if (this.anim) {
      const t = Math.min(1, (performance.now() - this.anim.start) / this.anim.dur);
      const k = ease(t);
      const lerp = (a: number[], b: number[]) => a.map((v, i) => v + (b[i] - v) * k) as [number, number, number];
      this.camera.position.set(...lerp(this.anim.from.position, this.anim.to.position));
      this.controls.target.set(...lerp(this.anim.from.target, this.anim.to.target));
      if (t >= 1) {
        this.anim = null;
        this.events.onCameraChange?.(this.cameraState());
      }
      this.needsRender = true;
    }
    const moved = this.controls.update();
    if (moved) this.needsRender = true;
    if (!this.needsRender || (this.holdFrames && !moved && !this.anim)) return;
    this.needsRender = false;
    this.renderer.render(this.scene, this.camera);
    // "First meaningful render" = the skeleton is on screen (F01-AC1).
    if (!this.firstRendered && [...this.byId.values()].some((ps) => ps[0]?.layer === 'skeletal')) {
      this.firstRendered = true;
      this.events.onFirstRender?.();
    }
  }

  /** Largest square image the GPU can render in one pass (texture, renderbuffer and viewport limits). */
  maxImageSize(): number {
    const gl = this.renderer.getContext();
    const vp = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array;
    return Math.min(this.renderer.capabilities.maxTextureSize, gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number, vp[0], vp[1]);
  }

  /** Aspect ratio (w/h) of the part of the canvas not covered by the card panel or sheet. */
  visibleAspect(): number {
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    return Math.max(1, w - this.insets.right) / Math.max(1, h - this.insets.bottom);
  }

  /**
   * Renders the current view (same camera, layers, selection) at `width`×`height` and copies it to a 2D canvas.
   * The frame covers exactly the visible region of the screen, so the image matches what the user sees.
   * Points in `anchors` are projected to image pixels (label leader lines). UI chrome is DOM, so it never appears.
   */
  renderImage(opts: { width: number; height: number; background: string | null; anchors?: Array<[number, number, number]> }): {
    canvas: HTMLCanvasElement;
    anchors: Array<{ x: number; y: number } | null>;
  } {
    const r = this.renderer;
    const w = this.container.clientWidth || 1;
    const h = this.container.clientHeight || 1;
    const { right, bottom } = this.insets;
    const prevRatio = r.getPixelRatio();
    r.setPixelRatio(1);
    r.setSize(opts.width, opts.height, false);
    // Same virtual frustum as the screen (see resize), cropped to the visible region.
    this.camera.aspect = w / h;
    this.camera.setViewOffset(w, h, right / 2, bottom / 2, w - right, h - bottom);
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();
    if (opts.background) r.setClearColor(new Color(opts.background), 1);
    else r.setClearColor(0x000000, 0);
    r.render(this.scene, this.camera);
    // Read back in the same task as the render: the drawing buffer is still valid (preserveDrawingBuffer is off).
    const out = document.createElement('canvas');
    out.width = r.domElement.width;
    out.height = r.domElement.height;
    // A CPU-backed canvas: one synchronous readback here, then cheap PNG encoding (a GPU canvas stalls behind the GL queue).
    out.getContext('2d', { willReadFrequently: true })!.drawImage(r.domElement, 0, 0);
    const anchors = (opts.anchors ?? []).map((pt) => {
      const v = new Vector3(...pt).project(this.camera);
      if (v.z > 1) return null;
      return { x: ((v.x + 1) / 2) * out.width, y: ((1 - v.y) / 2) * out.height };
    });
    r.setClearColor(0x000000, 0);
    r.setPixelRatio(prevRatio);
    this.resize();
    // Render the screen again in this task so the browser never composites the export-sized frame.
    r.render(this.scene, this.camera);
    return { canvas: out, anchors };
  }

  stats(): { meshes: number; visible: number; triangles: number; calls: number } {
    let visible = 0, triangles = 0;
    for (const p of this.parts.values()) if (p.mesh.visible) {
      visible++;
      triangles += (p.mesh.geometry.index?.count ?? 0) / 3;
    }
    return { meshes: this.parts.size, visible, triangles, calls: this.renderer.info.render.calls };
  }
}
